import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Sparkles, Loader2, Copy, Check, FileText, ListChecks,
  Layers, MessageSquareText, Wand2, Send, Download, History, Clock, Network,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { exportSummaryPdf, exportQuizPdf, exportFlashcardsPdf } from "@/lib/exportPdf";
import Markdown from "@/components/Markdown";
import AudioSummary from "@/components/AudioSummary";
import QuizRunner from "@/components/QuizRunner";
import Flashcards from "@/components/Flashcards";
import ConceptMap from "@/components/ConceptMap";

const FORMATS = [
  { id: "concise", label: "Conciso" },
  { id: "detailed", label: "Dettagliato" },
  { id: "keypoints", label: "Punti chiave" },
];

export default function MaterialDetail() {
  const { id } = useParams();
  const [mat, setMat] = useState(null);

  useEffect(() => { api.getMaterial(id).then(setMat).catch(() => toast.error("Materiale non trovato")); }, [id]);

  if (!mat) {
    return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      <Link to="/" data-testid="back-link" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Torna ai materiali
      </Link>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">{mat.title}</h1>
        <details className="group mb-6">
          <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground list-none inline-flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" /> Mostra il testo originale
          </summary>
          <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap bg-secondary rounded-xl p-4 max-h-64 overflow-auto">{mat.content}</p>
        </details>

        <Tabs defaultValue="summary">
          <TabsList className="grid grid-cols-5 w-full max-w-2xl mb-6">
            <TabsTrigger value="summary" data-testid="tab-summary" className="gap-1.5"><FileText className="h-3.5 w-3.5" /><span className="hidden sm:inline">Riassunto</span></TabsTrigger>
            <TabsTrigger value="quiz" data-testid="tab-quiz" className="gap-1.5"><ListChecks className="h-3.5 w-3.5" /><span className="hidden sm:inline">Quiz</span></TabsTrigger>
            <TabsTrigger value="flashcards" data-testid="tab-flashcards" className="gap-1.5"><Layers className="h-3.5 w-3.5" /><span className="hidden sm:inline">Flashcard</span></TabsTrigger>
            <TabsTrigger value="open" data-testid="tab-open" className="gap-1.5"><MessageSquareText className="h-3.5 w-3.5" /><span className="hidden sm:inline">Domande</span></TabsTrigger>
            <TabsTrigger value="map" data-testid="tab-map" className="gap-1.5"><Network className="h-3.5 w-3.5" /><span className="hidden sm:inline">Mappa</span></TabsTrigger>
          </TabsList>

          <TabsContent value="summary"><SummaryPanel mat={mat} setMat={setMat} /></TabsContent>
          <TabsContent value="quiz"><QuizPanel mat={mat} setMat={setMat} /></TabsContent>
          <TabsContent value="flashcards"><FlashcardPanel mat={mat} setMat={setMat} /></TabsContent>
          <TabsContent value="open"><OpenPanel mat={mat} setMat={setMat} /></TabsContent>
          <TabsContent value="map"><ConceptMapPanel mat={mat} setMat={setMat} /></TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-sm">{children}</div>;
}

function SummaryPanel({ mat, setMat }) {
  const [fmt, setFmt] = useState("concise");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const current = mat.summaries?.[fmt];

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.summary(mat.id, fmt);
      setMat((m) => ({ ...m, summaries: { ...(m.summaries || {}), [fmt]: res.content } }));
    } catch { toast.error("Errore nella generazione."); }
    finally { setLoading(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(current || "");
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {FORMATS.map((f) => (
          <button
            key={f.id}
            data-testid={`summary-format-${f.id}`}
            onClick={() => setFmt(f.id)}
            className={`px-3.5 py-1.5 rounded-xl text-sm font-medium transition-colors ${fmt === f.id ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-secondary/70"}`}
          >{f.label}</button>
        ))}
      </div>

      {current ? (
        <div>
          <div className="flex justify-end gap-1 mb-2">
            <AudioSummary text={current} />
            <Button data-testid="export-summary-button" variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={() => exportSummaryPdf(mat.title, FORMATS.find((f) => f.id === fmt)?.label, current)}>
              <Download className="h-3.5 w-3.5" /> PDF
            </Button>
            <Button data-testid="copy-summary-button" variant="ghost" size="sm" className="gap-1.5 text-xs" onClick={copy}>
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copiato" : "Copia"}
            </Button>
          </div>
          <div data-testid="summary-content"><Markdown text={current} /></div>
          <Button data-testid="regenerate-summary-button" variant="secondary" size="sm" className="rounded-xl gap-2 mt-5" onClick={generate} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Rigenera
          </Button>
        </div>
      ) : (
        <EmptyGenerate
          testid="generate-summary-button"
          text="Genera un riassunto del materiale in questo formato."
          loading={loading}
          onClick={generate}
          label="Genera Riassunto"
        />
      )}
    </Card>
  );
}

function QuizPanel({ mat, setMat }) {
  const [loading, setLoading] = useState(false);
  const [useTimer, setUseTimer] = useState(false);
  const [runKey, setRunKey] = useState(0);
  const [history, setHistory] = useState([]);
  const questions = mat.quiz;

  const loadHistory = () => api.getAttempts(mat.id).then(setHistory).catch(() => {});
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [mat.id]);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.quiz(mat.id, 6);
      setMat((m) => ({ ...m, quiz: res.questions }));
      setRunKey((k) => k + 1);
    } catch { toast.error("Errore nella generazione del quiz."); }
    finally { setLoading(false); }
  };

  const onComplete = async ({ score, total, duration }) => {
    try {
      await api.saveAttempt(mat.id, { score, total, duration_seconds: duration });
      loadHistory();
      toast.success(`Punteggio salvato: ${score}/${total}`);
    } catch { /* silent */ }
  };

  const best = history.length ? Math.max(...history.map((h) => Math.round((h.score / h.total) * 100))) : null;

  return (
    <Card>
      {questions && questions.length > 0 ? (
        <div>
          <QuizRunner key={runKey} questions={questions} timer={useTimer} onComplete={onComplete} />
          <div className="mt-6 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Switch id="timer-switch" data-testid="quiz-timer-toggle" checked={useTimer} onCheckedChange={(v) => { setUseTimer(v); setRunKey((k) => k + 1); }} />
              <Label htmlFor="timer-switch" className="text-sm flex items-center gap-1.5 cursor-pointer"><Clock className="h-3.5 w-3.5" /> Timer</Label>
            </div>
            <div className="flex gap-1">
              <Button data-testid="export-quiz-button" variant="ghost" size="sm" className="rounded-xl gap-1.5 text-xs" onClick={() => exportQuizPdf(mat.title, questions)}>
                <Download className="h-3.5 w-3.5" /> PDF
              </Button>
              <Button data-testid="new-quiz-button" variant="secondary" size="sm" className="rounded-xl gap-2" onClick={generate} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Nuovo quiz
              </Button>
            </div>
          </div>

          {history.length > 0 && (
            <div className="mt-6" data-testid="quiz-history">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
                <History className="h-3.5 w-3.5" /> Storico ({history.length}) {best !== null && <span className="text-primary">• Miglior {best}%</span>}
              </div>
              <div className="space-y-1.5 max-h-52 overflow-auto">
                {history.map((h) => {
                  const pct = Math.round((h.score / h.total) * 100);
                  return (
                    <div key={h.id} className="flex items-center justify-between text-sm bg-secondary rounded-lg px-3 py-2">
                      <span className="text-muted-foreground">{new Date(h.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      <span className="flex items-center gap-3">
                        {h.duration_seconds > 0 && <span className="text-muted-foreground text-xs inline-flex items-center gap-1"><Clock className="h-3 w-3" />{Math.floor(h.duration_seconds / 60)}:{String(h.duration_seconds % 60).padStart(2, "0")}</span>}
                        <span className={`font-semibold ${pct >= 70 ? "text-emerald-600" : pct >= 40 ? "text-amber-600" : "text-destructive"}`}>{h.score}/{h.total} ({pct}%)</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Switch id="timer-switch-empty" data-testid="quiz-timer-toggle-empty" checked={useTimer} onCheckedChange={setUseTimer} />
            <Label htmlFor="timer-switch-empty" className="text-sm flex items-center gap-1.5 cursor-pointer"><Clock className="h-3.5 w-3.5" /> Attiva timer</Label>
          </div>
          <EmptyGenerate
            testid="generate-quiz-button"
            text="Genera un quiz a risposta multipla con punteggio e spiegazioni."
            loading={loading}
            onClick={generate}
            label="Genera Quiz"
          />
        </div>
      )}
    </Card>
  );
}

function FlashcardPanel({ mat, setMat }) {
  const [loading, setLoading] = useState(false);
  const cards = mat.flashcards;

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.flashcards(mat.id, 8);
      setMat((m) => ({ ...m, flashcards: res.flashcards }));
    } catch { toast.error("Errore nella generazione delle flashcard."); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      {cards && cards.length > 0 ? (
        <div>
          <Flashcards cards={cards} />
          <div className="mt-6 pt-4 border-t border-border flex justify-center gap-1">
            <Button data-testid="export-flashcards-button" variant="ghost" size="sm" className="rounded-xl gap-1.5 text-xs" onClick={() => exportFlashcardsPdf(mat.title, cards)}>
              <Download className="h-3.5 w-3.5" /> PDF
            </Button>
            <Button data-testid="new-flashcards-button" variant="secondary" size="sm" className="rounded-xl gap-2" onClick={generate} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Nuove flashcard
            </Button>
          </div>
        </div>
      ) : (
        <EmptyGenerate
          testid="generate-flashcards-button"
          text="Genera flashcard fronte/retro per il ripasso attivo."
          loading={loading}
          onClick={generate}
          label="Genera Flashcard"
        />
      )}
    </Card>
  );
}

function OpenPanel({ mat, setMat }) {
  const [loading, setLoading] = useState(false);
  const questions = mat.open_questions;

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.openQuestions(mat.id, 6);
      setMat((m) => ({ ...m, open_questions: res.questions }));
    } catch { toast.error("Errore nella generazione delle domande."); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      {questions && questions.length > 0 ? (
        <div className="space-y-4">
          {questions.map((q, i) => <OpenQuestion key={i} matId={mat.id} index={i} question={q} />)}
          <div className="pt-2 flex justify-center">
            <Button data-testid="new-open-questions-button" variant="secondary" size="sm" className="rounded-xl gap-2" onClick={generate} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Nuove domande
            </Button>
          </div>
        </div>
      ) : (
        <EmptyGenerate
          testid="generate-open-questions-button"
          text="Genera domande aperte e ricevi una valutazione AI delle tue risposte."
          loading={loading}
          onClick={generate}
          label="Genera Domande Aperte"
        />
      )}
    </Card>
  );
}

function OpenQuestion({ matId, index, question }) {
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const evaluate = async () => {
    if (!answer.trim()) { toast.error("Scrivi una risposta."); return; }
    setLoading(true);
    try {
      const res = await api.evaluate(matId, question, answer.trim());
      setResult(res);
    } catch { toast.error("Errore nella valutazione."); }
    finally { setLoading(false); }
  };

  const scoreColor = result ? (result.score >= 70 ? "text-emerald-600" : result.score >= 40 ? "text-amber-600" : "text-destructive") : "";

  return (
    <div className="border border-border rounded-xl p-4" data-testid={`open-question-${index}`}>
      <p className="font-medium text-[15px] mb-3">{index + 1}. {question}</p>
      <Textarea
        data-testid={`open-answer-${index}`}
        placeholder="Scrivi qui la tua risposta..."
        className="min-h-[90px] mb-3"
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
      />
      <Button data-testid={`evaluate-answer-${index}`} size="sm" className="rounded-xl gap-2" onClick={evaluate} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Valuta risposta
      </Button>

      {result && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-4 bg-secondary rounded-xl p-4 space-y-3 text-sm" data-testid={`evaluation-result-${index}`}>
          <div className={`font-display font-bold text-lg ${scoreColor}`}>Punteggio: {result.score}/100</div>
          {result.correct?.length > 0 && (
            <div><span className="font-semibold text-emerald-600">Punti corretti:</span>
              <ul className="list-disc pl-5 mt-1 text-muted-foreground">{result.correct.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </div>
          )}
          {result.missing?.length > 0 && (
            <div><span className="font-semibold text-amber-600">Da approfondire:</span>
              <ul className="list-disc pl-5 mt-1 text-muted-foreground">{result.missing.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </div>
          )}
          {result.feedback && <p className="text-muted-foreground"><span className="font-semibold text-foreground">Consiglio: </span>{result.feedback}</p>}
        </motion.div>
      )}
    </div>
  );
}

function ConceptMapPanel({ mat, setMat }) {
  const [loading, setLoading] = useState(false);
  const map = mat.concept_map;

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.conceptMap(mat.id);
      setMat((m) => ({ ...m, concept_map: res }));
    } catch { toast.error("Errore nella generazione della mappa."); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      {map && map.branches?.length > 0 ? (
        <div>
          <ConceptMap map={map} />
          <div className="mt-6 pt-4 border-t border-border flex justify-center">
            <Button data-testid="new-concept-map-button" variant="secondary" size="sm" className="rounded-xl gap-2" onClick={generate} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Rigenera mappa
            </Button>
          </div>
        </div>
      ) : (
        <EmptyGenerate
          testid="generate-concept-map-button"
          text="Genera una mappa concettuale visiva dei concetti chiave del materiale."
          loading={loading}
          onClick={generate}
          label="Genera Mappa"
        />
      )}
    </Card>
  );
}

function EmptyGenerate({ testid, text, loading, onClick, label }) {
  return (
    <div className="text-center py-10">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-accent flex items-center justify-center mb-4">
        <Sparkles className="h-7 w-7 text-primary" />
      </div>
      <p className="text-sm text-muted-foreground max-w-xs mx-auto mb-5">{text}</p>
      <Button data-testid={testid} onClick={onClick} disabled={loading} className="rounded-xl gap-2">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {loading ? "Generazione..." : label}
      </Button>
    </div>
  );
}
